import {
  MOBILE_MEMBERSHIP_FEATURES,
  MOBILE_MEMBERSHIP_NOTICES,
  MOBILE_MEMBERSHIP_SUPPORTS,
  PC_MEMBERSHIP_NOTICES,
} from './mobile-membership-data';

type MembershipSectionsVariant = 'mobile' | 'pc';

type MembershipFeatureSectionProps = {
  variant?: MembershipSectionsVariant;
};

type MembershipSupportSectionProps = {
  variant?: MembershipSectionsVariant;
};

type MembershipNoticeSectionProps = {
  variant?: MembershipSectionsVariant;
};

export const MembershipFeatureSection = ({ variant = 'mobile' }: MembershipFeatureSectionProps) => {
  if (variant === 'pc') {
    const pcFeatures = [...MOBILE_MEMBERSHIP_FEATURES].sort(
      (currentFeature, nextFeature) =>
        (currentFeature.pcSortOrder ?? MOBILE_MEMBERSHIP_FEATURES.indexOf(currentFeature)) -
        (nextFeature.pcSortOrder ?? MOBILE_MEMBERSHIP_FEATURES.indexOf(nextFeature)),
    );

    return (
      <section className="space-y-4" aria-labelledby="membership-feature-title">
        <h2 id="membership-feature-title" className="text-center text-gr-900 Title3S18">
          한눈에 보는 <span className="text-point-500">멤버십 혜택</span>
        </h2>
        <div className="grid grid-cols-3 gap-4 max-[1180px]:grid-cols-2 max-[760px]:grid-cols-1">
          {pcFeatures.map(({ iconSrc, pcValue, title, value }) => {
            const displayValue = pcValue ?? value;

            return (
              <div
                key={title}
                className="flex min-h-[96px] min-w-0 flex-col items-start gap-2 rounded-[20px] border border-gr-200 bg-wh p-4"
              >
                <img src={iconSrc} alt="" className="h-8 w-8 shrink-0" aria-hidden="true" />
                <div className="flex w-full min-w-0 flex-col gap-1">
                  <h3 className="min-w-0 break-keep text-gr-900 Title3S18">{title}</h3>
                  {displayValue && (
                    <p className="break-keep text-point-500 Body5M14">{displayValue}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-3" aria-labelledby="membership-feature-title">
      <h2 id="membership-feature-title" className="text-gr-900 Body1S16">
        한눈에 보는 <span className="text-point-500">멤버십 기능</span>
      </h2>
      <div className="space-y-2">
        {MOBILE_MEMBERSHIP_FEATURES.map(({ iconSrc, title, value }) => (
          <div
            key={title}
            className="flex min-h-[58px] items-center justify-between gap-3 rounded-[20px] border border-gr-200 bg-wh p-3"
          >
            <div className="flex min-w-0 items-center gap-2">
              <img src={iconSrc} alt="" className="h-8 w-8 shrink-0" aria-hidden="true" />
              <span className="min-w-0 break-keep text-gr-800 Body1S16">{title}</span>
            </div>
            {value && (
              <span className="shrink-0 whitespace-nowrap text-point-500 Body5M14">{value}</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export const MembershipSupportSection = ({ variant = 'mobile' }: MembershipSupportSectionProps) => {
  const isPc = variant === 'pc';

  return (
    <section
      className={isPc ? 'space-y-4' : 'space-y-3'}
      aria-labelledby="membership-support-title"
    >
      <h2
        id="membership-support-title"
        className={isPc ? 'text-gr-800 Body5M14' : 'text-gr-800 Body4S14'}
      >
        지원 안내
      </h2>
      <div
        className={
          isPc
            ? 'grid grid-cols-2 gap-4 max-[760px]:grid-cols-1'
            : 'space-y-2 rounded-[16px] bg-gr-50 p-4'
        }
      >
        {MOBILE_MEMBERSHIP_SUPPORTS.map((support) => (
          <div
            key={support.title}
            className={
              isPc
                ? 'min-w-0 space-y-1 rounded-[20px] border border-gr-200 bg-gr-100 p-4'
                : 'space-y-1'
            }
          >
            <h3 className={isPc ? 'break-keep text-gr-800 Body1S16' : 'text-gr-800 Body4S14'}>
              {support.title}
            </h3>
            <p className="break-keep text-gr-600 Body5M14">{support.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const MembershipNoticeSection = ({ variant = 'mobile' }: MembershipNoticeSectionProps) => {
  if (variant === 'pc') {
    return (
      <section className="space-y-3 pb-10" aria-labelledby="membership-notice-title">
        <h2 id="membership-notice-title" className="text-gr-800 Body5M14">
          유의 사항
        </h2>
        <ul className="space-y-0.5 text-gr-600 Body5M14">
          {PC_MEMBERSHIP_NOTICES.map((notice) => (
            <li key={notice}>· {notice}</li>
          ))}
        </ul>
      </section>
    );
  }

  return (
    <section className="space-y-3 pb-10" aria-labelledby="membership-notice-title">
      <h2 id="membership-notice-title" className="text-gr-800 Body4S14">
        유의사항
      </h2>
      <ul className="list-disc space-y-1 pl-5 text-gr-600 Body5M14">
        {MOBILE_MEMBERSHIP_NOTICES.map((notice) => (
          <li key={notice}>{notice}</li>
        ))}
      </ul>
    </section>
  );
};
